/**
 * A port's one series over a window (one series per port, S3, 2026-10-26; backend
 * platform-core/internal/twin/port_series_route.go, contract api/openapi.yaml TwinPortSeries).
 *
 *   GET /v1/twin/entities/{entity_id}/ports/{port}/series?from&to&bucket
 *
 * A port fed by several sources (a machine's roles list its `sources`) reads, for each stretch of time, one source: its
 * source of record. This is that series in the port's unit, as the resolver reads it, so a board never draws one of the
 * sources' tags for a port or two of them at once. A window of at most 500 readings gives each reading (`agg` "raw");
 * a denser one gives a bucket's mean and count per `bucket`. `latest` is the port's current value whatever the window,
 * in its unit. A window wider than 400 days reads its most recent 400 (`from` says where it starts).
 *
 * A port with two or more sources and no decided stretches yet (the analysis refresh makes the first decision) reads its
 * older part, all but the last day, from its first choice alone and says `deciding`: the chart is approximate there until
 * the port is decided.
 *
 *   400 invalid_range    from or to does not read, or from is not before to
 *   400 invalid_bucket   a bucket that does not read, under a second, over a week, or too small for the window
 *   404 entity_not_found no machine of this org with that id
 */
import { api } from "@/lib/api-client";
import { qs } from "./qs";

/** One point of a port's chart, in the port's unit: a reading (no count), or a bucket's mean and its readings. */
export interface PortSeriesPoint {
  t: string;
  value: number;
  count?: number;
}

/** The port's current value, in its unit, and the tag it was read from. */
export interface PortSeriesLatest {
  t: string;
  value: number;
  tag_id: string;
  /** False for a reading known only to its plant day (t is the day's noon). */
  time_known: boolean;
}

export interface PortSeries {
  entity_id: string;
  port: string;
  /** The port's unit: the catalog parameter's, else its first ranked source's. */
  unit: string;
  from: string;
  to: string;
  agg: "raw" | "avg";
  /** 0 when every reading is given. */
  bucket_seconds: number;
  points: PortSeriesPoint[];
  latest: PortSeriesLatest | null;
  /** How many sources the port may read: 0 when nothing feeds it. */
  sources: number;
  /**
   * True when the older part of the window is read from the port's first choice alone, because the port is not decided
   * yet (two or more sources, no stretches): approximate there until the analysis refresh decides it.
   */
  deciding: boolean;
}

const enc = encodeURIComponent;

export const portSeriesPath = (entityId: string, port: string) =>
  `/v1/twin/entities/${enc(entityId)}/ports/${enc(port)}/series`;

/** A port's series over [from, to), in buckets of `bucket` ("15 minutes", "1 day", "2h"); a Go nil list as empty. */
export async function readPortSeries(
  entityId: string,
  port: string,
  params: { from: string; to: string; bucket?: string },
): Promise<PortSeries> {
  const raw = await api.get<PortSeries>(`${portSeriesPath(entityId, port)}${qs(params)}`);
  return { ...raw, points: raw?.points ?? [], latest: raw?.latest ?? null, deciding: raw?.deciding === true };
}
